import { useState, useEffect } from "react";

/**
 * PageShell — wraps a page with:
 * - a Bloomberg-style ticker strip on top
 * - an editorial masthead (eyebrow · big serif title · description · optional CTA)
 * - the page content
 * - a bottom colophon rule with brand + page label
 *
 * Ticker items: [{ label, value, live?: boolean, mono?: boolean }, ...]
 * `title` can be a string or JSX (use <em className="italic text-blue-700">…</em> for accents).
 */
export default function PageShell({
  ticker = [],
  eyebrow,
  title,
  description,
  cta,
  colophon,
  maxWidth = "1400px",
  children,
}) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);

  const dateStr = now.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="min-h-screen flex flex-col">
      {/* Ticker */}
      <div
        className="border-b border-slate-200 px-4 sm:px-10 py-2 flex items-center justify-between gap-3 text-[11px] font-mono-tabular text-slate-500 no-print"
        style={{ background: "linear-gradient(180deg, #fff 0%, #fafaf9 100%)" }}
      >
        <div className="flex items-center gap-4 sm:gap-6 min-w-0 overflow-hidden">
          {ticker.slice(0, Math.ceil(ticker.length / 2)).map((t, i) => (
            <div key={i} className="flex items-center gap-2 shrink-0">
              {i > 0 && <span className="text-slate-300 hidden sm:inline">|</span>}
              {t.live && (
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              )}
              <span className="uppercase tracking-[0.24em] text-[10px]">
                {t.label}{" "}
                <span className={`text-slate-800 ${t.mono ? "font-mono-tabular" : ""}`}>
                  {t.value}
                </span>
              </span>
            </div>
          ))}
        </div>
        <div className="hidden md:flex items-center gap-6">
          {ticker.slice(Math.ceil(ticker.length / 2)).map((t, i) => (
            <div key={i} className="flex items-center gap-2">
              {i > 0 && <span className="text-slate-300">|</span>}
              <span className="uppercase tracking-[0.24em] text-[10px]">
                {t.label}{" "}
                <span className={`text-slate-800 ${t.mono ? "font-mono-tabular" : ""}`}>
                  {t.value}
                </span>
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Masthead */}
      <div className="px-4 sm:px-10 pt-6 sm:pt-10 pb-6 sm:pb-8 no-print">
        <div className="flex items-end justify-between gap-6 flex-wrap" style={{ maxWidth }}>
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-3 sm:mb-4">
              <div className="h-px w-6 sm:w-10 bg-slate-900" />
              <div className="text-[9.5px] sm:text-[10px] uppercase tracking-[0.32em] text-slate-500">
                {eyebrow || dateStr}
              </div>
            </div>
            <h1 className="font-serif text-[36px] sm:text-[48px] lg:text-[56px] leading-[0.95] text-slate-900 tracking-tight">
              {title}
            </h1>
            {description && (
              <p className="text-sm text-slate-500 mt-3 sm:mt-4 max-w-lg leading-relaxed">
                {description}
              </p>
            )}
          </div>
          {cta && <div className="shrink-0 w-full sm:w-auto">{cta}</div>}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 sm:px-10 flex-1" style={{ maxWidth }}>
        {children}
      </div>

      {/* Colophon */}
      <div className="mt-10 px-4 sm:px-10 py-4 border-t border-slate-200 no-print">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.28em] text-slate-400 flex-wrap gap-2">
          <span>Afridi&amp;Co. · Heavy Machinery Auto Parts</span>
          <span className="font-mono-tabular">
            {colophon || "End of section"} · {now.getFullYear()}
          </span>
        </div>
        <div className="mt-2 flex items-center justify-center text-[10px] uppercase tracking-[0.24em] text-slate-400 text-center">
          <span>
            Software developed by{" "}
            <span className="text-slate-600 font-medium">Code Wave Solutions</span>
            <span className="mx-2 text-slate-300">·</span>
            <span className="font-mono-tabular text-slate-600">0348 6811636</span>
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * SectionMark — roman numeral + label + rule
 */
export function SectionMark({ roman, label }) {
  return (
    <div className="flex items-center gap-4 mb-5">
      <div className="font-serif italic text-blue-700 text-[15px] leading-none">{roman}</div>
      <div className="text-[10px] uppercase tracking-[0.32em] text-slate-500">{label}</div>
      <div className="flex-1 h-px bg-slate-200" />
    </div>
  );
}
